/**
 * AccessSummary Component
 *
 * Slim landing-page strip showing which instances the logged-in operator can migrate between.
 * The server already filters /api/connected-services to their allowed_user_instances, so this
 * renders exactly what every instance picker will offer. What the operator can DO is not
 * repeated here — the migration cards below are already gated by capability.
 *
 * @module components/AccessSummary
 */

import React from "react";
import { Loader2, Server } from "lucide-react";
import { useInstances } from "../lib/api-central-mu";
import { useAuthStore } from "../store/useAuthStore";

export const AccessSummary: React.FC = () => {
  const { isDefaultUser } = useAuthStore();
  const { data: instances, isLoading: instancesLoading, isError: instancesError } = useInstances();

  return (
    <section
      className="mb-8 flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card/60 px-4 py-2.5"
      title={
        isDefaultUser
          ? "As administrator you manage all registered instances."
          : "Migrations can only use the instances listed here."
      }
    >
      <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground/70">
        <Server className="h-3.5 w-3.5" />
        Your instances
      </span>
      {instancesLoading ? (
        <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Loading…
        </span>
      ) : instancesError ? (
        <span className="text-xs text-destructive">Couldn't load your instance access.</span>
      ) : instances && instances.length > 0 ? (
        instances.map((instance) => (
          <span
            key={instance.id}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs text-foreground"
          >
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                instance.active ? "bg-emerald-500" : "bg-muted-foreground/50"
              }`}
            />
            {instance.name}
          </span>
        ))
      ) : (
        <span className="text-xs text-muted-foreground">
          No instance access yet — contact your administrator.
        </span>
      )}
    </section>
  );
};

export default AccessSummary;
